import { Component, computed, inject, input } from '@angular/core';
import { ParseRequestsStore } from '../../stores/parse-requests.store';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { OrganizationSidebarLayout } from '../../../organizations/components/organization-sidebar-layout/organization-sidebar-layout';
import { PageHeader } from '../../../layout/components/page-header/page-header';
import { Button, Skeleton, Alert, NotificationService, ConfirmService } from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { PageTitle } from '../../../layout/components/page-title/page-title';
import { ParseRequestsDetailsBreadcrumbNav } from '../../components/parse-requests-details-breadcrumb-nav/parse-requests-details-breadcrumb-nav';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ParseRequestsDetailsNav } from '../../components/parse-requests-details-nav/parse-requests-details-nav';
import { HasRoleDirective } from '../../../../shared/directives/has-role';
import { DatePipe } from '@angular/common';
import { SupplierContractWizardStore } from '../../../supplier-contracts/stores/supplier-contract-wizard.store';

@Component({
  selector: 'app-parse-requests-details-page',
  imports: [
    OrganizationSidebarLayout,
    PageHeader,
    Button,
    RouterLink,
    NgIcon,
    PageTitle,
    Skeleton,
    ParseRequestsDetailsBreadcrumbNav,
    RouterOutlet,
    ErrorPage,
    TranslocoPipe,
    ParseRequestsDetailsNav,
    HasRoleDirective,
    Alert,
    DatePipe,
  ],
  templateUrl: './parse-requests-details-page.html',
})
export class ParseRequestsDetailsPage {
  private store = inject(ParseRequestsStore);
  protected router = inject(Router);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private confirmService = inject(ConfirmService);
  private supplierContractWizardStore = inject(SupplierContractWizardStore);

  organizationId = input.required<string>();
  parseRequestId = input.required<string>();

  state = computed(() => {
    const parseRequestId = this.parseRequestId();
    return this.store.parseRequests()?.find((p) => p.id === parseRequestId);
  });

  isUpdateLoading = computed(() => this.state()?.isUpdateLoading ?? false);
  protected updateError = computed(() => this.state()?.updateError ?? null);

  protected isDraftsLoading = this.supplierContractWizardStore.isDraftsLoading;
  protected draftsError = this.supplierContractWizardStore.draftsError;
  private drafts = computed(() => {
    const parseRequestId = this.parseRequestId();
    return this.supplierContractWizardStore
      .drafts()
      .filter((draft) => draft.data?.parseRequestId === parseRequestId);
  });

  protected async onClickComplete() {
    if (this.drafts().length > 0) {
      void this.confirmService.confirm({
        allowCancel: false,
        confirmButtonText: this.translocoService.translate(
          'cockpit.parseRequests.details.completeDraftsError.confirmButton',
        ),
        icon: 'heroExclamationTriangle',
        title: this.translocoService.translate(
          'cockpit.parseRequests.details.completeDraftsError.title',
        ),
        message: this.translocoService.translate(
          'cockpit.parseRequests.details.completeDraftsError.message',
        ),
      });
      return;
    }
    if (
      !(await this.confirmService.confirm({
        allowCancel: true,
        cancelButtonText: this.translocoService.translate(
          'cockpit.parseRequests.details.completeConfirm.cancelButton',
        ),
        confirmButtonText: this.translocoService.translate(
          'cockpit.parseRequests.details.completeConfirm.confirmButton',
        ),
        title: this.translocoService.translate('cockpit.parseRequests.details.completeButton'),
        message: this.translocoService.translate(
          'cockpit.parseRequests.details.completeConfirm.message',
        ),
      }))
    ) {
      return;
    }
    await this.store.complete(this.parseRequestId());
    this.notificationService.showSuccess({
      title: this.translocoService.translate('cockpit.parseRequests.details.completeSuccess.title'),
    });
  }

  async onClickPickup() {
    if (
      !(await this.confirmService.confirm({
        allowCancel: true,
        cancelButtonText: this.translocoService.translate(
          'cockpit.parseRequests.details.pickupConfirm.cancelButton',
        ),
        confirmButtonText: this.translocoService.translate(
          'cockpit.parseRequests.details.pickupConfirm.confirmButton',
        ),
        title: this.translocoService.translate('cockpit.parseRequests.details.pickupButton'),
        message: this.translocoService.translate(
          'cockpit.parseRequests.details.pickupConfirm.message',
        ),
      }))
    ) {
      return;
    }
    await this.store.pickup(this.parseRequestId());
    this.notificationService.showSuccess({
      title: this.translocoService.translate('cockpit.parseRequests.details.pickupSuccess.title'),
      subtitle: this.translocoService.translate(
        'cockpit.parseRequests.details.pickupSuccess.subtitle',
      ),
    });
  }
}
